import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useDocumentApi from "hooks/api/private/useDocumentApi";
import { useDocumentId } from "hooks/useId";
import useSingleFile from "hooks/useSingleFile";

import { DocumentBody, DocumentDetails } from "types/api/document";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { fileNotEmptyValidator, fileProperties, fileSizeValidator, getExtraErrorsDescription, maxLength100Validator } from "utils/validators";

export type DocumentFormType = {
    id?: string;
    enabled: boolean;
    attachment: AttachmentType;
    text: {
        it: {
            title: string;
            description: string;
        };
        en: {
            title: string;
            description: string;
        };
    };
};

export type DocumentFormHook = {
    setSingleFile: (fileToSet: AttachmentType, path: string) => void;
    formHook: FormHookType<DocumentFormType>;
};

function useDocumentForm(): DocumentFormHook {
    const documentId = useDocumentId();

    const { createDocument, updateDocument, getOneDocument } = useDocumentApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: { type: "simple", validators: [] },
                enabled: { type: "simple", validators: [] },
                attachment: {
                    type: "object",
                    validators: [fileNotEmptyValidator, fileSizeValidator],
                    properties: fileProperties,
                },
                text: {
                    type: "object",
                    properties: {
                        it: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [notEmptyValidator] },
                                description: { type: "simple", validators: [maxLength100Validator] },
                            },
                        },
                        en: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [] },
                                description: { type: "simple", validators: [maxLength100Validator] },
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: DocumentFormType = React.useMemo(
        () => ({
            enabled: true,
            attachment: createAttachmentType({ label: "Allegato PDF" }),
            text: {
                it: { title: "", description: "" },
                en: { title: "", description: "" },
            },
        }),
        [],
    );

    const formHook = useForm<DocumentFormType>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const [documentDetails, setDocumentDetails] = React.useState<DocumentDetails | undefined>(undefined);

    React.useEffect(() => {
        if (documentId) {
            const [apiCall, abortController] = getOneDocument({ id: documentId });

            apiCall.then(setDocumentDetails);

            return () => { abortController.abort(); };
        }
    }, [getOneDocument, documentId]);

    React.useEffect(() => {
        if (documentDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = documentDetails.id;
                newForm.enabled = documentDetails.enabled;
                newForm.text.it.title = documentDetails.text.it?.title ?? "";
                newForm.text.it.description = documentDetails.text.it?.description ?? "";
                newForm.text.en.title = documentDetails.text.en?.title ?? "";
                newForm.text.en.description = documentDetails.text.en?.description ?? "";

                if (documentDetails.attachment) {
                    newForm.attachment = createAttachmentType({
                        id: documentDetails.attachment.id,
                        label: "Allegato PDF",
                        filename: documentDetails.attachment.filename,
                        status: 2,
                    });
                }

                return newForm;
            });
        }
    }, [setForm, documentDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("useDocumentForm", () => {
            if (isValid) {
                const body: DocumentBody = {
                    enabled: form.enabled,
                    attachment: converterAttachmentToFileWithPayload(form.attachment),
                    text: {
                        it: {
                            title: form.text.it.title,
                            description: form.text.it.description,
                        },
                        en: {
                            title: form.text.en.title,
                            description: form.text.en.description,
                        },
                    },
                };

                if (documentId) {
                    const [apiCall] = updateDocument({ id: documentId }, body);
                    return apiCall;
                } else {
                    const [apiCall] = createDocument(body);
                    return apiCall;
                }
            } else {
                showAllError();
                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, createDocument, updateDocument, isValid, documentId, form]);

    const { setSingleFile } = useSingleFile({ setForm });

    return React.useMemo(
        (): DocumentFormHook => ({
            setSingleFile,
            formHook,
        }),
        [setSingleFile, formHook],
    );
}

export default useDocumentForm;
